import React, { useState, useEffect } from "react";
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  Lock,
  Clock,
  Key,
  Mail,
  Hand,
  Smartphone,
  Laptop,
  Globe,
  Trash2,
  Check,
  X,
  ChevronRight,
  RefreshCw,
  AlertTriangle,
  UserX,
  UserCheck,
  Search,
  ExternalLink,
  Info,
  Radio,
  Sliders,
  Settings,
  HelpCircle
} from "lucide-react";
import { TelegramAccount } from "../types";

interface PrivacySecurityModalProps {
  isOpen: boolean;
  onClose: () => void;
  account: TelegramAccount | null;
  accounts?: TelegramAccount[];
  onSelectAccount?: (acc: TelegramAccount) => void;
  showFeedback: (msg: string, type?: "success" | "error" | "info") => void;
  onAccountUpdated?: () => void;
}

export interface PrivacySettingsData {
  twoStepVerification: {
    enabled: boolean;
    hasPassword: boolean;
    hint?: string;
    hasRecovery?: boolean;
    emailUnconfirmedPattern?: string;
    loginEmail?: string;
  };
  autoDeleteMessages: {
    period: number; // 0 = off, 86400 = 1 day, 604800 = 1 week, 2678400 = 1 month
  };
  passcodeLock: {
    enabled: boolean;
  };
  passkey: {
    enabled: boolean;
  };
  loginEmail: string;
  blockedUsersCount: number;
  activeAuthorizationsCount: number;
  suggestTopContacts: boolean;
  ttlDays: number; // 30, 90, 180, 365, 548 (18 months), 730 (24 months)
  privacy: {
    voiceMessages: "all" | "contacts" | "nobody";
    messages: "all" | "contacts" | "nobody";
    birthday: "all" | "contacts" | "nobody";
    gifts: "all" | "contacts" | "nobody";
    bio: "all" | "contacts" | "nobody";
    savedMusic: "all" | "contacts" | "nobody";
    chatInvites: "all" | "contacts" | "nobody";
    phoneNumber: "all" | "contacts" | "nobody";
    lastSeen: "all" | "contacts" | "nobody";
    profilePhoto: "all" | "contacts" | "nobody";
    forwards: "all" | "contacts" | "nobody";
    phoneCalls: "all" | "contacts" | "nobody";
  };
}

export interface ActiveSession {
  hash: string;
  deviceModel: string;
  platform: string;
  systemVersion: string;
  apiId: number;
  appName: string;
  appVersion: string;
  dateCreated: number;
  dateActive: number;
  ip: string;
  country: string;
  region: string;
  current: boolean;
}

export interface BlockedUser {
  id: string;
  firstName: string;
  lastName?: string;
  username?: string;
  phone?: string;
  date?: number;
}

export const PrivacySecurityModal: React.FC<PrivacySecurityModalProps> = ({
  isOpen,
  onClose,
  account,
  accounts = [],
  onSelectAccount,
  showFeedback,
  onAccountUpdated
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [updatingField, setUpdatingField] = useState<string | null>(null);
  const [data, setData] = useState<PrivacySettingsData | null>(null);

  // Sub-modals state
  const [activeSubModal, setActiveSubModal] = useState<
    | null
    | "2fa"
    | "devices"
    | "blocked"
    | "autodelete"
    | "ttl"
    | "passcode"
    | "privacy_picker"
  >(null);

  const [activePrivacyPickerKey, setActivePrivacyPickerKey] = useState<
    keyof PrivacySettingsData["privacy"] | null
  >(null);

  // Sessions and Blocked list
  const [devicesList, setDevicesList] = useState<ActiveSession[]>([]);
  const [loadingDevices, setLoadingDevices] = useState<boolean>(false);
  const [blockedList, setBlockedList] = useState<BlockedUser[]>([]);
  const [loadingBlocked, setLoadingBlocked] = useState<boolean>(false);
  const [blockedSearchQuery, setBlockedSearchQuery] = useState<string>("");

  // 2FA Form
  const [twoFaCurrentPass, setTwoFaCurrentPass] = useState<string>("");
  const [twoFaNewPass, setTwoFaNewPass] = useState<string>("");
  const [twoFaConfirmPass, setTwoFaConfirmPass] = useState<string>("");
  const [twoFaHint, setTwoFaHint] = useState<string>("");
  const [twoFaSubmitting, setTwoFaSubmitting] = useState<boolean>(false);

  // Passcode Lock Form (client security feature)
  const [passcodeVal, setPasscodeVal] = useState<string>("");
  const [passcodeConfirm, setPasscodeConfirm] = useState<string>("");

  const phone = account?.phoneNumber || "";

  const getAuthHeaders = (extra: Record<string, string> = {}) => {
    const token = localStorage.getItem("auth_token");
    const headers: Record<string, string> = { ...extra };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
  };

  // Fetch privacy settings when opened or account changed
  const fetchPrivacySettings = async () => {
    if (!phone) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/accounts/privacy-security?phone=${encodeURIComponent(phone)}`, {
        headers: getAuthHeaders()
      });
      const json = await res.json();
      if (!res.ok || json.error) {
        throw new Error(json.error || "获取隐私与安全设置失败");
      }
      setData(json.data);
    } catch (err: any) {
      console.error("fetchPrivacySettings error:", err);
      showFeedback(err.message || "无法加载隐私设置", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && phone) {
      fetchPrivacySettings();
    }
  }, [isOpen, phone]);

  // Load devices
  const fetchDevices = async () => {
    if (!phone) return;
    setLoadingDevices(true);
    try {
      const res = await fetch(`/api/accounts/privacy-security/devices?phone=${encodeURIComponent(phone)}`, {
        headers: getAuthHeaders()
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error || "获取设备列表失败");
      setDevicesList(json.devices || []);
    } catch (err: any) {
      showFeedback(err.message || "获取设备列表失败", "error");
    } finally {
      setLoadingDevices(false);
    }
  };

  // Load blocked users
  const fetchBlockedUsers = async () => {
    if (!phone) return;
    setLoadingBlocked(true);
    try {
      const res = await fetch(`/api/accounts/privacy-security/blocked?phone=${encodeURIComponent(phone)}`, {
        headers: getAuthHeaders()
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error || "获取已屏蔽用户失败");
      setBlockedList(json.blocked || []);
    } catch (err: any) {
      showFeedback(err.message || "获取已屏蔽用户失败", "error");
    } finally {
      setLoadingBlocked(false);
    }
  };

  // Terminate session
  const handleTerminateSession = async (hash: string, terminateAllOther = false) => {
    if (!phone) return;
    const confirmMsg = terminateAllOther
      ? "确定要注销并终止除当前设备外的所有其他活跃登录会话吗？"
      : "确定要注销并终止该设备会话吗？";
    if (!window.confirm(confirmMsg)) return;

    setUpdatingField("terminate_session");
    try {
      const res = await fetch("/api/accounts/privacy-security/terminate-session", {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ phone, hash, terminateAllOther })
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error || "终止会话失败");
      showFeedback(terminateAllOther ? "已成功终止其他所有会话" : "已终止该设备会话", "success");
      fetchDevices();
      fetchPrivacySettings();
    } catch (err: any) {
      showFeedback(err.message || "终止会话失败", "error");
    } finally {
      setUpdatingField(null);
    }
  };

  // Unblock user
  const handleUnblockUser = async (userId: string) => {
    if (!phone) return;
    setUpdatingField(`unblock_${userId}`);
    try {
      const res = await fetch("/api/accounts/privacy-security/unblock", {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ phone, userId })
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error || "解除屏蔽失败");
      showFeedback("已成功解除屏蔽", "success");
      fetchBlockedUsers();
      fetchPrivacySettings();
    } catch (err: any) {
      showFeedback(err.message || "解除屏蔽失败", "error");
    } finally {
      setUpdatingField(null);
    }
  };

  // Update privacy value
  const handleUpdatePrivacyValue = async (
    key: keyof PrivacySettingsData["privacy"],
    val: "all" | "contacts" | "nobody"
  ) => {
    if (!phone || !data) return;
    setUpdatingField(`privacy_${key}`);
    try {
      const res = await fetch("/api/accounts/privacy-security/update-privacy", {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ phone, key, value: val })
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error || "更新隐私权限失败");
      
      setData(prev => prev ? {
        ...prev,
        privacy: { ...prev.privacy, [key]: val }
      } : null);

      showFeedback("隐私设置已更新", "success");
      setActiveSubModal(null);
      setActivePrivacyPickerKey(null);
    } catch (err: any) {
      showFeedback(err.message || "更新隐私设置失败", "error");
    } finally {
      setUpdatingField(null);
    }
  };

  // Update TTL
  const handleUpdateTTL = async (days: number) => {
    if (!phone || !data) return;
    setUpdatingField("ttl");
    try {
      const res = await fetch("/api/accounts/privacy-security/update-ttl", {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ phone, days })
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error || "更新保留期限失败");
      
      setData(prev => prev ? { ...prev, ttlDays: days } : null);
      showFeedback("帐号保留期限已更新", "success");
      setActiveSubModal(null);
    } catch (err: any) {
      showFeedback(err.message || "更新保留期限失败", "error");
    } finally {
      setUpdatingField(null);
    }
  };

  // Update Auto-Delete messages TTL
  const handleUpdateAutoDelete = async (period: number) => {
    if (!phone || !data) return;
    setUpdatingField("autodelete");
    try {
      const res = await fetch("/api/accounts/privacy-security/update-autodelete", {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ phone, period })
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error || "更新自动删除设置失败");
      
      setData(prev => prev ? { ...prev, autoDeleteMessages: { period } } : null);
      showFeedback("消息记录自动删除设置已更新", "success");
      setActiveSubModal(null);
    } catch (err: any) {
      showFeedback(err.message || "更新自动删除设置失败", "error");
    } finally {
      setUpdatingField(null);
    }
  };

  // Toggle Top Contacts
  const handleToggleTopContacts = async (enabled: boolean) => {
    if (!phone || !data) return;
    setUpdatingField("top_contacts");
    try {
      const res = await fetch("/api/accounts/privacy-security/toggle-top-contacts", {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ phone, enabled })
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error || "切换常用联系人推荐失败");
      
      setData(prev => prev ? { ...prev, suggestTopContacts: enabled } : null);
      showFeedback(enabled ? "已开启推荐常用联系人" : "已关闭推荐常用联系人", "success");
    } catch (err: any) {
      showFeedback(err.message || "设置失败", "error");
    } finally {
      setUpdatingField(null);
    }
  };

  // Clear payment and shipping info
  const handleClearPayments = async () => {
    if (!phone) return;
    if (!window.confirm("确定要清空此账号所有已保存的付款和配送信息吗？")) return;
    setUpdatingField("clear_payments");
    try {
      const res = await fetch("/api/accounts/privacy-security/clear-payments", {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ phone })
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error || "清空付款信息失败");
      showFeedback("已成功清空付款和配送信息", "success");
    } catch (err: any) {
      showFeedback(err.message || "清空付款信息失败", "error");
    } finally {
      setUpdatingField(null);
    }
  };

  // 2FA Submit
  const handleSave2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone) return;

    if (twoFaNewPass && twoFaNewPass !== twoFaConfirmPass) {
      showFeedback("新两步验证密码与确认密码不一致", "error");
      return;
    }

    setTwoFaSubmitting(true);
    try {
      const res = await fetch("/api/accounts/privacy-security/update-2fa", {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          phone,
          currentPassword: twoFaCurrentPass,
          newPassword: twoFaNewPass,
          hint: twoFaHint
        })
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error || "更新两步验证失败");
      
      showFeedback(twoFaNewPass ? "已成功设置两步验证密码" : "已关闭两步验证密码", "success");
      setTwoFaCurrentPass("");
      setTwoFaNewPass("");
      setTwoFaConfirmPass("");
      setTwoFaHint("");
      setActiveSubModal(null);
      fetchPrivacySettings();
      if (onAccountUpdated) onAccountUpdated();
    } catch (err: any) {
      showFeedback(err.message || "更新两步验证失败", "error");
    } finally {
      setTwoFaSubmitting(false);
    }
  };

  if (!isOpen || !account) return null;

  // Format Helper for Privacy options
  const formatPrivacyLabel = (val: "all" | "contacts" | "nobody" | undefined) => {
    if (val === "contacts") return "我的联系人";
    if (val === "nobody") return "无人";
    return "所有人";
  };

  // Format Helper for Auto-Delete
  const formatAutoDeleteLabel = (period: number) => {
    if (!period || period === 0) return "关闭";
    if (period <= 86400) return "1 天";
    if (period <= 604800) return "1 周";
    if (period <= 2678400) return "1 个月";
    return `${Math.round(period / 86400)} 天`;
  };

  // Format Helper for TTL
  const formatTTLLabel = (days: number) => {
    if (days >= 730) return "24 个月";
    if (days >= 548) return "18 个月";
    if (days >= 365) return "12 个月";
    if (days >= 180) return "6 个月";
    if (days >= 90) return "3 个月";
    return "1 个月";
  };

  const privacyFieldTitles: Record<keyof PrivacySettingsData["privacy"], string> = {
    voiceMessages: "语音留言",
    messages: "消息",
    birthday: "生日",
    gifts: "礼物",
    bio: "个人简介",
    savedMusic: "保存的音乐",
    chatInvites: "被邀请权限",
    phoneNumber: "电话号码",
    lastSeen: "最后上线与在线时间",
    profilePhoto: "个人头像",
    forwards: "转发消息来源",
    phoneCalls: "语音通话"
  };

  return (
    <div
      id="modal-privacy-security"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm overflow-y-auto animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="relative w-full max-w-lg bg-[#17212b] border border-slate-700/80 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh] text-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800 bg-[#1c2733] sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-purple-500/15 border border-purple-500/30 flex items-center justify-center text-purple-400">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white flex items-center gap-2">
                隐私与安全
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 font-normal">
                  Telegram 官方设置
                </span>
              </h2>
              <p className="text-xs text-slate-400 font-mono">
                {account.firstName || account.username || account.phoneNumber} ({account.phoneNumber})
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={fetchPrivacySettings}
              disabled={loading}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800/60 rounded-lg transition-colors"
              title="刷新当前账号隐私配置"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin text-purple-400" : ""}`} />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800/60 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Account Selector (if multiple accounts) */}
        {accounts && accounts.length > 1 && onSelectAccount && (
          <div className="px-5 py-2.5 bg-[#0e1621] border-b border-slate-800/80 flex items-center justify-between text-xs">
            <span className="text-slate-400">切换正在配置的账号:</span>
            <select
              value={account.phoneNumber}
              onChange={(e) => {
                const target = accounts.find((a) => a.phoneNumber === e.target.value);
                if (target) onSelectAccount(target);
              }}
              className="bg-slate-800 text-slate-200 border border-slate-700 rounded-lg px-2.5 py-1 text-xs focus:outline-none focus:border-purple-500"
            >
              {accounts.map((acc) => (
                <option key={acc.phoneNumber} value={acc.phoneNumber}>
                  {acc.firstName || acc.username || acc.phoneNumber} ({acc.phoneNumber})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Main Content Area */}
        <div className="overflow-y-auto p-4 space-y-5 text-sm">
          {loading && !data ? (
            <div className="py-16 flex flex-col items-center justify-center gap-3 text-slate-400">
              <RefreshCw className="w-8 h-8 text-purple-400 animate-spin" />
              <p className="text-sm">正在实时同步 Telegram 官方安全与隐私策略...</p>
            </div>
          ) : (
            <>
              {/* 1. 安全 (Security Section) */}
              <div className="space-y-1.5">
                <div className="px-2 text-xs font-semibold text-purple-400 uppercase tracking-wider">
                  安全
                </div>
                <div className="bg-[#1e2c3a]/70 rounded-xl border border-slate-800 divide-y divide-slate-800/60 overflow-hidden shadow-sm">
                  {/* 两步验证 (Two-Step Verification) */}
                  <button
                    type="button"
                    onClick={() => setActiveSubModal("2fa")}
                    className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-slate-800/40 transition-colors text-left group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-lg bg-purple-500/10 flex items-center justify-center text-purple-400">
                        <Shield className="w-4 h-4" />
                      </div>
                      <span className="font-medium text-slate-200 group-hover:text-white">两步验证</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs font-medium">
                      <span
                        className={
                          data?.twoStepVerification?.hasPassword
                            ? "text-purple-400 font-semibold"
                            : "text-slate-400"
                        }
                      >
                        {data?.twoStepVerification?.hasPassword ? "开启" : "关闭"}
                      </span>
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </div>
                  </button>

                  {/* 消息记录自动删除 */}
                  <button
                    type="button"
                    onClick={() => setActiveSubModal("autodelete")}
                    className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-slate-800/40 transition-colors text-left group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-lg bg-sky-500/10 flex items-center justify-center text-sky-400">
                        <Clock className="w-4 h-4" />
                      </div>
                      <span className="font-medium text-slate-200 group-hover:text-white">
                        消息记录自动删除
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs font-medium">
                      <span className="text-purple-400 font-medium">
                        {formatAutoDeleteLabel(data?.autoDeleteMessages?.period || 0)}
                      </span>
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </div>
                  </button>

                  {/* 设置锁定码 */}
                  <button
                    type="button"
                    onClick={() => setActiveSubModal("passcode")}
                    className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-slate-800/40 transition-colors text-left group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-lg bg-indigo-500/10 flex items-center justify-center text-indigo-400">
                        <Lock className="w-4 h-4" />
                      </div>
                      <span className="font-medium text-slate-200 group-hover:text-white">设置锁定码</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs font-medium">
                      <span className="text-purple-400">
                        {data?.passcodeLock?.enabled ? "开启" : "关闭"}
                      </span>
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </div>
                  </button>

                  {/* 通行密钥 */}
                  <div className="w-full px-4 py-3.5 flex items-center justify-between text-left">
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-400">
                        <Key className="w-4 h-4" />
                      </div>
                      <span className="font-medium text-slate-200">通行密钥</span>
                    </div>
                    <div className="text-xs text-slate-500">已就绪</div>
                  </div>

                  {/* 邮箱登录 */}
                  <div className="w-full px-4 py-3.5 flex items-center justify-between text-left">
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400">
                        <Mail className="w-4 h-4" />
                      </div>
                      <span className="font-medium text-slate-200">邮箱登录</span>
                    </div>
                    <div className="text-xs font-mono text-purple-400">
                      {data?.twoStepVerification?.loginEmail ||
                        data?.twoStepVerification?.emailUnconfirmedPattern ||
                        "未绑定"}
                    </div>
                  </div>

                  {/* 已屏蔽的用户 */}
                  <button
                    type="button"
                    onClick={() => {
                      fetchBlockedUsers();
                      setActiveSubModal("blocked");
                    }}
                    className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-slate-800/40 transition-colors text-left group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-lg bg-rose-500/10 flex items-center justify-center text-rose-400">
                        <Hand className="w-4 h-4" />
                      </div>
                      <span className="font-medium text-slate-200 group-hover:text-white">已屏蔽的用户</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs font-semibold">
                      <span className="text-purple-400">{data?.blockedUsersCount ?? 0}</span>
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </div>
                  </button>

                  {/* 显示所有设备 / 活跃设备 */}
                  <button
                    type="button"
                    onClick={() => {
                      fetchDevices();
                      setActiveSubModal("devices");
                    }}
                    className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-slate-800/40 transition-colors text-left group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400">
                        <Laptop className="w-4 h-4" />
                      </div>
                      <span className="font-medium text-slate-200 group-hover:text-white">显示所有设备</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs font-semibold">
                      <span className="text-purple-400">{data?.activeAuthorizationsCount ?? 1}</span>
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </div>
                  </button>
                </div>
                <p className="text-[11px] text-slate-500 px-2 leading-relaxed">
                  管理您所有设备上的登录状态。
                </p>
              </div>

              {/* 2. 隐私 (Privacy Permissions Section) */}
              <div className="space-y-1.5 pt-2">
                <div className="px-2 text-xs font-semibold text-purple-400 uppercase tracking-wider">
                  隐私
                </div>
                <div className="bg-[#1e2c3a]/70 rounded-xl border border-slate-800 divide-y divide-slate-800/60 overflow-hidden shadow-sm">
                  {/* 语音留言 */}
                  <button
                    type="button"
                    onClick={() => {
                      setActivePrivacyPickerKey("voiceMessages");
                      setActiveSubModal("privacy_picker");
                    }}
                    className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-slate-800/40 transition-colors text-left group"
                  >
                    <span className="font-medium text-slate-200 group-hover:text-white">语音留言</span>
                    <div className="flex items-center gap-1.5 text-xs font-medium">
                      <span className="text-purple-400">
                        {formatPrivacyLabel(data?.privacy?.voiceMessages)}
                      </span>
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </div>
                  </button>

                  {/* 消息 / 私聊 */}
                  <button
                    type="button"
                    onClick={() => {
                      setActivePrivacyPickerKey("messages");
                      setActiveSubModal("privacy_picker");
                    }}
                    className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-slate-800/40 transition-colors text-left group"
                  >
                    <span className="font-medium text-slate-200 group-hover:text-white">消息</span>
                    <div className="flex items-center gap-1.5 text-xs font-medium">
                      <span className="text-purple-400">
                        {formatPrivacyLabel(data?.privacy?.messages)}
                      </span>
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </div>
                  </button>

                  {/* 生日 */}
                  <button
                    type="button"
                    onClick={() => {
                      setActivePrivacyPickerKey("birthday");
                      setActiveSubModal("privacy_picker");
                    }}
                    className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-slate-800/40 transition-colors text-left group"
                  >
                    <span className="font-medium text-slate-200 group-hover:text-white">生日</span>
                    <div className="flex items-center gap-1.5 text-xs font-medium">
                      <span className="text-purple-400">
                        {formatPrivacyLabel(data?.privacy?.birthday)}
                      </span>
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </div>
                  </button>

                  {/* 礼物 */}
                  <button
                    type="button"
                    onClick={() => {
                      setActivePrivacyPickerKey("gifts");
                      setActiveSubModal("privacy_picker");
                    }}
                    className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-slate-800/40 transition-colors text-left group"
                  >
                    <span className="font-medium text-slate-200 group-hover:text-white">礼物</span>
                    <div className="flex items-center gap-1.5 text-xs font-medium">
                      <span className="text-purple-400">
                        {formatPrivacyLabel(data?.privacy?.gifts)}
                      </span>
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </div>
                  </button>

                  {/* 个人简介 */}
                  <button
                    type="button"
                    onClick={() => {
                      setActivePrivacyPickerKey("bio");
                      setActiveSubModal("privacy_picker");
                    }}
                    className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-slate-800/40 transition-colors text-left group"
                  >
                    <span className="font-medium text-slate-200 group-hover:text-white">个人简介</span>
                    <div className="flex items-center gap-1.5 text-xs font-medium">
                      <span className="text-purple-400">
                        {formatPrivacyLabel(data?.privacy?.bio)}
                      </span>
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </div>
                  </button>

                  {/* 保存的音乐 */}
                  <button
                    type="button"
                    onClick={() => {
                      setActivePrivacyPickerKey("savedMusic");
                      setActiveSubModal("privacy_picker");
                    }}
                    className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-slate-800/40 transition-colors text-left group"
                  >
                    <span className="font-medium text-slate-200 group-hover:text-white">保存的音乐</span>
                    <div className="flex items-center gap-1.5 text-xs font-medium">
                      <span className="text-purple-400">
                        {formatPrivacyLabel(data?.privacy?.savedMusic)}
                      </span>
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </div>
                  </button>

                  {/* 被邀请权限 */}
                  <button
                    type="button"
                    onClick={() => {
                      setActivePrivacyPickerKey("chatInvites");
                      setActiveSubModal("privacy_picker");
                    }}
                    className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-slate-800/40 transition-colors text-left group"
                  >
                    <span className="font-medium text-slate-200 group-hover:text-white">被邀请权限</span>
                    <div className="flex items-center gap-1.5 text-xs font-medium">
                      <span className="text-purple-400">
                        {formatPrivacyLabel(data?.privacy?.chatInvites)}
                      </span>
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </div>
                  </button>

                  {/* 电话号码 */}
                  <button
                    type="button"
                    onClick={() => {
                      setActivePrivacyPickerKey("phoneNumber");
                      setActiveSubModal("privacy_picker");
                    }}
                    className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-slate-800/40 transition-colors text-left group"
                  >
                    <span className="font-medium text-slate-200 group-hover:text-white">电话号码</span>
                    <div className="flex items-center gap-1.5 text-xs font-medium">
                      <span className="text-purple-400">
                        {formatPrivacyLabel(data?.privacy?.phoneNumber)}
                      </span>
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </div>
                  </button>

                  {/* 最后上线与在线时间 */}
                  <button
                    type="button"
                    onClick={() => {
                      setActivePrivacyPickerKey("lastSeen");
                      setActiveSubModal("privacy_picker");
                    }}
                    className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-slate-800/40 transition-colors text-left group"
                  >
                    <span className="font-medium text-slate-200 group-hover:text-white">最后上线与在线时间</span>
                    <div className="flex items-center gap-1.5 text-xs font-medium">
                      <span className="text-purple-400">
                        {formatPrivacyLabel(data?.privacy?.lastSeen)}
                      </span>
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </div>
                  </button>

                  {/* 个人头像 */}
                  <button
                    type="button"
                    onClick={() => {
                      setActivePrivacyPickerKey("profilePhoto");
                      setActiveSubModal("privacy_picker");
                    }}
                    className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-slate-800/40 transition-colors text-left group"
                  >
                    <span className="font-medium text-slate-200 group-hover:text-white">个人头像</span>
                    <div className="flex items-center gap-1.5 text-xs font-medium">
                      <span className="text-purple-400">
                        {formatPrivacyLabel(data?.privacy?.profilePhoto)}
                      </span>
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </div>
                  </button>

                  {/* 转发消息来源 */}
                  <button
                    type="button"
                    onClick={() => {
                      setActivePrivacyPickerKey("forwards");
                      setActiveSubModal("privacy_picker");
                    }}
                    className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-slate-800/40 transition-colors text-left group"
                  >
                    <span className="font-medium text-slate-200 group-hover:text-white">转发消息来源</span>
                    <div className="flex items-center gap-1.5 text-xs font-medium">
                      <span className="text-purple-400">
                        {formatPrivacyLabel(data?.privacy?.forwards)}
                      </span>
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </div>
                  </button>

                  {/* 语音通话 */}
                  <button
                    type="button"
                    onClick={() => {
                      setActivePrivacyPickerKey("phoneCalls");
                      setActiveSubModal("privacy_picker");
                    }}
                    className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-slate-800/40 transition-colors text-left group"
                  >
                    <span className="font-medium text-slate-200 group-hover:text-white">语音通话</span>
                    <div className="flex items-center gap-1.5 text-xs font-medium">
                      <span className="text-purple-400">
                        {formatPrivacyLabel(data?.privacy?.phoneCalls)}
                      </span>
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </div>
                  </button>
                </div>
              </div>

              {/* 3. 机器人与网站 (Bots & Websites) */}
              <div className="space-y-1.5 pt-2">
                <div className="px-2 text-xs font-semibold text-purple-400 uppercase tracking-wider">
                  机器人与网站
                </div>
                <div className="bg-[#1e2c3a]/70 rounded-xl border border-slate-800 divide-y divide-slate-800/60 overflow-hidden shadow-sm">
                  <button
                    type="button"
                    onClick={handleClearPayments}
                    disabled={updatingField === "clear_payments"}
                    className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-slate-800/40 transition-colors text-left group"
                  >
                    <span className="font-medium text-slate-200 group-hover:text-white">
                      清空付款和配送信息
                    </span>
                    {updatingField === "clear_payments" ? (
                      <RefreshCw className="w-4 h-4 text-purple-400 animate-spin" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    )}
                  </button>
                </div>
              </div>

              {/* 4. 常用联系人 (Frequent Contacts) */}
              <div className="space-y-1.5 pt-2">
                <div className="px-2 text-xs font-semibold text-purple-400 uppercase tracking-wider">
                  常用联系人
                </div>
                <div className="bg-[#1e2c3a]/70 rounded-xl border border-slate-800 p-4 shadow-sm flex items-center justify-between">
                  <span className="font-medium text-slate-200">推荐常用联系人</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Boolean(data?.suggestTopContacts)}
                      onChange={(e) => handleToggleTopContacts(e.target.checked)}
                      disabled={updatingField === "top_contacts"}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                  </label>
                </div>
                <p className="text-[11px] text-slate-500 px-2 leading-relaxed">
                  在搜索区域顶部显示常用联系人以便您快速访问。
                </p>
              </div>

              {/* 5. 帐号保留期限 (Account Self-Destruct TTL) */}
              <div className="space-y-1.5 pt-2">
                <div className="px-2 text-xs font-semibold text-purple-400 uppercase tracking-wider">
                  帐号保留期限
                </div>
                <div className="bg-[#1e2c3a]/70 rounded-xl border border-slate-800 divide-y divide-slate-800/60 overflow-hidden shadow-sm">
                  <button
                    type="button"
                    onClick={() => setActiveSubModal("ttl")}
                    className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-slate-800/40 transition-colors text-left group"
                  >
                    <span className="font-medium text-slate-200 group-hover:text-white">保留</span>
                    <div className="flex items-center gap-1.5 text-xs font-medium">
                      <span className="text-purple-400 font-semibold">
                        {formatTTLLabel(data?.ttlDays || 548)}
                      </span>
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </div>
                  </button>
                </div>
                <p className="text-[11px] text-slate-500 px-2 leading-relaxed">
                  若在此期间未上线，您的帐号及所有消息和联系人将被彻底删除。
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-800 bg-[#1c2733] flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-semibold transition-all shadow-md"
          >
            完成
          </button>
        </div>

        {/* ========================================================================= */}
        {/* SUB-MODAL 1: 两步验证 (2FA Setup / Change) */}
        {/* ========================================================================= */}
        {activeSubModal === "2fa" && (
          <div className="absolute inset-0 z-30 bg-[#17212b] flex flex-col animate-in slide-in-from-right-10 duration-200">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800 bg-[#1c2733]">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActiveSubModal(null)}
                  className="p-1 text-slate-400 hover:text-white rounded"
                >
                  <ChevronRight className="w-5 h-5 rotate-180" />
                </button>
                <h3 className="font-bold text-white text-sm">两步验证密码管理</h3>
              </div>
              <button
                type="button"
                onClick={() => setActiveSubModal(null)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSave2FA} className="p-5 space-y-4 overflow-y-auto flex-1 text-xs">
              <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-xl text-purple-300 space-y-1">
                <div className="flex items-center gap-1.5 font-semibold">
                  <ShieldCheck className="w-4 h-4" />
                  当前两步验证状态: {data?.twoStepVerification?.hasPassword ? "已开启" : "未开启"}
                </div>
                {data?.twoStepVerification?.hint && (
                  <p className="text-[11px] text-purple-400/90">
                    当前密码提示: {data.twoStepVerification.hint}
                  </p>
                )}
                <p className="text-[11px] text-slate-400">
                  为账号设置强密码，可在每次从新设备登录时保护账号不被盗用。
                </p>
              </div>

              {data?.twoStepVerification?.hasPassword && (
                <div>
                  <label className="block text-slate-300 mb-1 font-medium">
                    当前两步验证密码 <span className="text-rose-400">*</span>
                  </label>
                  <input
                    type="password"
                    placeholder="输入此账号当前的 2FA 密码"
                    value={twoFaCurrentPass}
                    onChange={(e) => setTwoFaCurrentPass(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-slate-300 mb-1 font-medium">
                  {data?.twoStepVerification?.hasPassword ? "新两步验证密码 (留空则关闭 2FA)" : "设置两步验证密码"}
                </label>
                <input
                  type="password"
                  placeholder="输入新密码"
                  value={twoFaNewPass}
                  onChange={(e) => setTwoFaNewPass(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                />
              </div>

              {twoFaNewPass && (
                <>
                  <div>
                    <label className="block text-slate-300 mb-1 font-medium">
                      确认新密码 <span className="text-rose-400">*</span>
                    </label>
                    <input
                      type="password"
                      placeholder="再次确认新密码"
                      value={twoFaConfirmPass}
                      onChange={(e) => setTwoFaConfirmPass(e.target.value)}
                      className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-slate-300 mb-1 font-medium">密码提示 (选填)</label>
                    <input
                      type="text"
                      placeholder="例如: 常用数字或密语"
                      value={twoFaHint}
                      onChange={(e) => setTwoFaHint(e.target.value)}
                      className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                    />
                  </div>
                </>
              )}

              <div className="pt-3 flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setActiveSubModal(null)}
                  className="px-4 py-2 rounded-xl bg-slate-800 text-slate-300 hover:bg-slate-700"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={twoFaSubmitting}
                  className="px-5 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-semibold flex items-center gap-1.5"
                >
                  {twoFaSubmitting && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                  保存两步验证
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ========================================================================= */}
        {/* SUB-MODAL 2: 活跃设备管理 (Devices / Sessions) */}
        {/* ========================================================================= */}
        {activeSubModal === "devices" && (
          <div className="absolute inset-0 z-30 bg-[#17212b] flex flex-col animate-in slide-in-from-right-10 duration-200">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800 bg-[#1c2733]">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActiveSubModal(null)}
                  className="p-1 text-slate-400 hover:text-white rounded"
                >
                  <ChevronRight className="w-5 h-5 rotate-180" />
                </button>
                <h3 className="font-bold text-white text-sm">活跃设备与会话</h3>
              </div>
              <button
                type="button"
                onClick={() => setActiveSubModal(null)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 flex-1 overflow-y-auto space-y-4 text-xs">
              {/* Terminate All Other Sessions Button */}
              {devicesList.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleTerminateSession("", true)}
                  disabled={updatingField === "terminate_session"}
                  className="w-full py-3 px-4 rounded-xl bg-rose-500/15 border border-rose-500/30 text-rose-300 hover:bg-rose-500/25 transition-all flex items-center justify-center gap-2 font-semibold shadow-sm"
                >
                  <Trash2 className="w-4 h-4 text-rose-400" />
                  终止除当前外的所有其他会话
                </button>
              )}

              {loadingDevices ? (
                <div className="py-12 flex flex-col items-center justify-center gap-2 text-slate-400">
                  <RefreshCw className="w-6 h-6 animate-spin text-purple-400" />
                  <span>正在获取所有登录设备...</span>
                </div>
              ) : devicesList.length === 0 ? (
                <div className="py-12 text-center text-slate-400">暂无活跃设备数据</div>
              ) : (
                <div className="space-y-3">
                  <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                    设备列表 ({devicesList.length})
                  </div>
                  {devicesList.map((dev, idx) => (
                    <div
                      key={dev.hash || idx}
                      className={`p-3.5 rounded-xl border transition-all ${
                        dev.current
                          ? "bg-purple-950/20 border-purple-500/40"
                          : "bg-slate-800/40 border-slate-700/60 hover:border-slate-600"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <div
                            className={`w-8 h-8 rounded-lg flex items-center justify-center mt-0.5 ${
                              dev.current
                                ? "bg-purple-500/20 text-purple-300"
                                : "bg-slate-700/50 text-slate-300"
                            }`}
                          >
                            {dev.platform?.toLowerCase().includes("android") ||
                            dev.platform?.toLowerCase().includes("ios") ? (
                              <Smartphone className="w-4 h-4" />
                            ) : (
                              <Laptop className="w-4 h-4" />
                            )}
                          </div>
                          <div>
                            <div className="font-bold text-white flex items-center gap-2">
                              {dev.deviceModel || dev.appName || "未知设备"}
                              {dev.current && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/30 text-purple-200 border border-purple-500/40">
                                  当前会话
                                </span>
                              )}
                            </div>
                            <div className="text-slate-400 text-[11px] mt-0.5">
                              {dev.appName} {dev.appVersion} • {dev.platform} {dev.systemVersion}
                            </div>
                            <div className="text-slate-500 text-[10px] font-mono mt-1 flex items-center gap-2">
                              <span>IP: {dev.ip || "保密"}</span>
                              {dev.country && <span>• {dev.country}</span>}
                              {dev.dateActive && (
                                <span>
                                  • 活跃时间: {new Date(dev.dateActive * 1000).toLocaleString()}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {!dev.current && dev.hash && (
                          <button
                            type="button"
                            onClick={() => handleTerminateSession(dev.hash)}
                            className="p-1.5 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 rounded-lg transition-colors shrink-0"
                            title="注销该设备"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* SUB-MODAL 3: 已屏蔽的用户 (Blocked Users) */}
        {/* ========================================================================= */}
        {activeSubModal === "blocked" && (
          <div className="absolute inset-0 z-30 bg-[#17212b] flex flex-col animate-in slide-in-from-right-10 duration-200">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800 bg-[#1c2733]">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActiveSubModal(null)}
                  className="p-1 text-slate-400 hover:text-white rounded"
                >
                  <ChevronRight className="w-5 h-5 rotate-180" />
                </button>
                <h3 className="font-bold text-white text-sm">已屏蔽的用户</h3>
              </div>
              <button
                type="button"
                onClick={() => setActiveSubModal(null)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 flex-1 overflow-y-auto space-y-3 text-xs">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                <input
                  type="text"
                  placeholder="搜索已屏蔽的用户..."
                  value={blockedSearchQuery}
                  onChange={(e) => setBlockedSearchQuery(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-9 pr-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 text-xs"
                />
              </div>

              {loadingBlocked ? (
                <div className="py-12 flex flex-col items-center justify-center gap-2 text-slate-400">
                  <RefreshCw className="w-6 h-6 animate-spin text-purple-400" />
                  <span>正在获取黑名单列表...</span>
                </div>
              ) : (
                <div className="space-y-2">
                  {blockedList
                    .filter((u) => {
                      if (!blockedSearchQuery) return true;
                      const q = blockedSearchQuery.toLowerCase();
                      return (
                        u.firstName?.toLowerCase().includes(q) ||
                        u.lastName?.toLowerCase().includes(q) ||
                        u.username?.toLowerCase().includes(q) ||
                        u.id?.includes(q)
                      );
                    })
                    .map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center justify-between p-3 bg-slate-800/40 border border-slate-700/60 rounded-xl hover:border-slate-600 transition-colors"
                      >
                        <div>
                          <div className="font-bold text-white">
                            {user.firstName} {user.lastName || ""}
                          </div>
                          <div className="text-slate-400 text-[11px] font-mono">
                            {user.username ? `@${user.username}` : `ID: ${user.id}`}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleUnblockUser(user.id)}
                          disabled={updatingField === `unblock_${user.id}`}
                          className="px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs font-medium transition-colors"
                        >
                          {updatingField === `unblock_${user.id}` ? "解除中..." : "解除屏蔽"}
                        </button>
                      </div>
                    ))}

                  {blockedList.length === 0 && (
                    <div className="py-12 text-center text-slate-400">黑名单为空</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* SUB-MODAL 4: 消息记录自动删除选择器 */}
        {/* ========================================================================= */}
        {activeSubModal === "autodelete" && (
          <div className="absolute inset-0 z-30 bg-[#17212b] flex flex-col animate-in slide-in-from-right-10 duration-200">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800 bg-[#1c2733]">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActiveSubModal(null)}
                  className="p-1 text-slate-400 hover:text-white rounded"
                >
                  <ChevronRight className="w-5 h-5 rotate-180" />
                </button>
                <h3 className="font-bold text-white text-sm">消息记录自动删除</h3>
              </div>
              <button
                type="button"
                onClick={() => setActiveSubModal(null)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-3 text-xs">
              <p className="text-slate-400 leading-relaxed">
                设置新发起对话的默认自毁/自动清除时间间隔：
              </p>
              {[
                { label: "关闭 (不自动删除)", value: 0 },
                { label: "1 天", value: 86400 },
                { label: "1 周 (7 天)", value: 604800 },
                { label: "1 个月 (31 天)", value: 2678400 }
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleUpdateAutoDelete(opt.value)}
                  className={`w-full p-3.5 rounded-xl border flex items-center justify-between transition-all ${
                    (data?.autoDeleteMessages?.period || 0) === opt.value
                      ? "bg-purple-950/30 border-purple-500 text-white font-bold"
                      : "bg-slate-800/40 border-slate-700/60 text-slate-300 hover:bg-slate-800"
                  }`}
                >
                  <span>{opt.label}</span>
                  {(data?.autoDeleteMessages?.period || 0) === opt.value && (
                    <Check className="w-4 h-4 text-purple-400" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* SUB-MODAL 5: 帐号保留期限选择器 */}
        {/* ========================================================================= */}
        {activeSubModal === "ttl" && (
          <div className="absolute inset-0 z-30 bg-[#17212b] flex flex-col animate-in slide-in-from-right-10 duration-200">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800 bg-[#1c2733]">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActiveSubModal(null)}
                  className="p-1 text-slate-400 hover:text-white rounded"
                >
                  <ChevronRight className="w-5 h-5 rotate-180" />
                </button>
                <h3 className="font-bold text-white text-sm">自毁保留期限 (Self-Destruct)</h3>
              </div>
              <button
                type="button"
                onClick={() => setActiveSubModal(null)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-3 text-xs">
              <p className="text-slate-400 leading-relaxed">
                若在此期间未上线，您的 Telegram 帐号及所有群组、联系人和消息将被 Telegram 服务器彻底删除。
              </p>
              {[
                { label: "1 个月 (30 天)", value: 30 },
                { label: "3 个月 (90 天)", value: 90 },
                { label: "6 个月 (180 天)", value: 180 },
                { label: "12 个月 (365 天)", value: 365 },
                { label: "18 个月 (548 天)", value: 548 },
                { label: "24 个月 (730 天)", value: 730 }
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleUpdateTTL(opt.value)}
                  className={`w-full p-3.5 rounded-xl border flex items-center justify-between transition-all ${
                    (data?.ttlDays || 548) === opt.value
                      ? "bg-purple-950/30 border-purple-500 text-white font-bold"
                      : "bg-slate-800/40 border-slate-700/60 text-slate-300 hover:bg-slate-800"
                  }`}
                >
                  <span>{opt.label}</span>
                  {(data?.ttlDays || 548) === opt.value && (
                    <Check className="w-4 h-4 text-purple-400" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* SUB-MODAL 6: 隐私权限选择器 (所有人 / 我的联系人 / 无人) */}
        {/* ========================================================================= */}
        {activeSubModal === "privacy_picker" && activePrivacyPickerKey && (
          <div className="absolute inset-0 z-30 bg-[#17212b] flex flex-col animate-in slide-in-from-right-10 duration-200">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800 bg-[#1c2733]">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setActiveSubModal(null);
                    setActivePrivacyPickerKey(null);
                  }}
                  className="p-1 text-slate-400 hover:text-white rounded"
                >
                  <ChevronRight className="w-5 h-5 rotate-180" />
                </button>
                <h3 className="font-bold text-white text-sm">
                  {privacyFieldTitles[activePrivacyPickerKey]}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => {
                  setActiveSubModal(null);
                  setActivePrivacyPickerKey(null);
                }}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-3 text-xs">
              <div className="text-slate-400 font-semibold uppercase tracking-wider text-[11px]">
                谁可以看到 / 使用我的 {privacyFieldTitles[activePrivacyPickerKey]}？
              </div>
              {[
                { label: "所有人", value: "all" as const },
                { label: "我的联系人", value: "contacts" as const },
                { label: "无人", value: "nobody" as const }
              ].map((opt) => {
                const isSelected = (data?.privacy?.[activePrivacyPickerKey] || "all") === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleUpdatePrivacyValue(activePrivacyPickerKey, opt.value)}
                    disabled={updatingField === `privacy_${activePrivacyPickerKey}`}
                    className={`w-full p-3.5 rounded-xl border flex items-center justify-between transition-all ${
                      isSelected
                        ? "bg-purple-950/30 border-purple-500 text-white font-bold"
                        : "bg-slate-800/40 border-slate-700/60 text-slate-300 hover:bg-slate-800"
                    }`}
                  >
                    <span>{opt.label}</span>
                    {isSelected && <Check className="w-4 h-4 text-purple-400" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* SUB-MODAL 7: 锁定码设置 (Passcode Lock) */}
        {/* ========================================================================= */}
        {activeSubModal === "passcode" && (
          <div className="absolute inset-0 z-30 bg-[#17212b] flex flex-col animate-in slide-in-from-right-10 duration-200">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800 bg-[#1c2733]">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActiveSubModal(null)}
                  className="p-1 text-slate-400 hover:text-white rounded"
                >
                  <ChevronRight className="w-5 h-5 rotate-180" />
                </button>
                <h3 className="font-bold text-white text-sm">设置锁定码</h3>
              </div>
              <button
                type="button"
                onClick={() => setActiveSubModal(null)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4 text-xs">
              <div className="p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-xl text-indigo-300 space-y-1">
                <div className="font-semibold">应用锁定码防护</div>
                <p className="text-[11px] text-slate-400">
                  设置 4 位数字或字母锁定码，离开控制面板或超时未操作时自动锁定。
                </p>
              </div>

              <div>
                <label className="block text-slate-300 mb-1 font-medium">输入锁定码</label>
                <input
                  type="password"
                  placeholder="例如: 1234"
                  value={passcodeVal}
                  onChange={(e) => setPasscodeVal(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-white text-xs placeholder-slate-500 focus:outline-none focus:border-purple-500"
                />
              </div>

              <div>
                <label className="block text-slate-300 mb-1 font-medium">确认锁定码</label>
                <input
                  type="password"
                  placeholder="再次输入以确认"
                  value={passcodeConfirm}
                  onChange={(e) => setPasscodeConfirm(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-white text-xs placeholder-slate-500 focus:outline-none focus:border-purple-500"
                />
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setActiveSubModal(null)}
                  className="px-4 py-2 rounded-xl bg-slate-800 text-slate-300 hover:bg-slate-700"
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (passcodeVal && passcodeVal !== passcodeConfirm) {
                      showFeedback("两次输入的锁定码不一致", "error");
                      return;
                    }
                    setData((prev) =>
                      prev
                        ? {
                            ...prev,
                            passcodeLock: { enabled: Boolean(passcodeVal) }
                          }
                        : null
                    );
                    showFeedback(passcodeVal ? "已成功设置锁定码" : "已关闭锁定码", "success");
                    setActiveSubModal(null);
                  }}
                  className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-semibold"
                >
                  保存设置
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
